/**
 * The one-object arms of the roster (`inspect`, `take`, `drop`, the wear
 * pair, `search`) — split off `narrow-parsed-command.ts` at the file-size
 * cap when D235 gave the roster its gift arm, the way `narrow-intercepts.ts`
 * and `narrow-instrument.ts` were. Nothing changed but the address.
 *
 * @packageDocumentation
 */

import type { RuntimeState } from '@orpheus/content-schema';
import type { ParsedCommand } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { CommandNarrowingOutcome } from './runtime-action.js';

import { speakSlot } from '../defaults/default-responses.js';
import { itemIdForEntityRef } from '../entities/entity-ref.js';
import { narrowed, rejected } from './narrow-parsed-command.js';
import { narrowTakeFrom } from './narrow-take-from.js';

/**
 * Narrow `search` — a named THING is gone through; nothing named cases the
 * room (D62).
 *
 * @param command - The parsed proposal
 * @param bundle - The loaded bundle
 * @param state - The live runtime state, when the caller has one
 * @returns The narrowed action, or the malformed-command rejection
 */
export const narrowSearchCommand = (
	command: ParsedCommand,
	bundle: EngineGameBundle,
	state: RuntimeState | undefined,
): CommandNarrowingOutcome => {
	const searchedRef = command.directObjectRef ?? command.indirectObjectRef;

	if (searchedRef === undefined) {
		return narrowed({ type: 'search_room' });
	}

	return narrowSingleObjectCommand(command, bundle, state, 'search');
};

/**
 * Narrow the single-object actions (`inspect`, `take`) — identical shape, one
 * direct object, the same malformed-command refusal when the grammar handed
 * us none.
 *
 * @param command - The parsed proposal
 * @param bundle - The loaded bundle (response table)
 * @param type - Which action to build
 * @returns The narrowed action, or the malformed-command rejection
 */
export const narrowSingleObjectCommand = (
	command: ParsedCommand,
	bundle: EngineGameBundle,
	state: RuntimeState | undefined,
	type: 'drop' | 'equip' | 'inspect' | 'search' | 'take' | 'unequip',
): CommandNarrowingOutcome => {
	if (command.directObjectRef === undefined) {
		return rejected(bundle, state, { detail: `${type} arrived without a direct object`, type: 'malformed_command' }, [
			speakSlot(bundle, state, 'malformedCommand'),
		]);
	}

	const itemId = itemIdForEntityRef(command.directObjectRef);

	// D242: `take <thing> from <place>` — the take when the thing is there (`narrow-take-from.ts`).
	return type === 'take' && command.indirectObjectRef !== undefined
		? narrowTakeFrom(bundle, state, itemId, command.indirectObjectRef)
		: narrowed({ itemId, type });
};
